import React, { useCallback, useRef } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Card,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { DynamicField } from "../../types";
import "./ClinicalFieldSelection.css";

import { useAppDispatch, useAppSelector } from "../../store";
import {
  endDiagnosticSession,
  addSystemMessage,
} from "../../store/thunks/sessionThunks";
import { selectCurrentSessionWithDetails } from "../../store/selectors";
import { clearDiagnosisCompleteness } from "../../store/slices/diagnosisSlice";

interface Props {
  fields: DynamicField[];
  selectedFieldId: string;
  onChange: (fieldId: string) => void; // parent callback
}

export const ClinicalFieldSelect: React.FC<Props> = ({
  fields,
  selectedFieldId,
  onChange,
}) => {
  const dispatch = useAppDispatch();
  const sessionDetails = useAppSelector(selectCurrentSessionWithDetails);
  const myInputRef = useRef<HTMLSelectElement>(null);
  /**
   * Handles a new field selection:
   * • Ends the current diagnostic session if the field actually changes
   * • Sends a system message + clears completeness
   * • Notifies parent via onChange
   */
  const handleFieldChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const fieldId = event.target.value as string;
      const field = fields.find((f) => f.field_id === fieldId);
      if (!field) return;

      if (sessionDetails && sessionDetails.field.field_id !== fieldId) {
        dispatch(endDiagnosticSession());
      }

      dispatch(
        addSystemMessage(
          `Switched to ${field.field_name}. Please select a case to begin.`
        )
      );
      dispatch(clearDiagnosisCompleteness());

      // bubble up to parent
      onChange(fieldId);
    },
    [fields, sessionDetails, dispatch, onChange]
  );

  return (
    <Card
      sx={{
        marginBottom: "20px",
        backgroundColor: "background.default",
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
      className="clinical-field-container"
    >
      <Box
        className="clinical-field-info"
        sx={{
          backgroundColor: "background.default",
          borderBottom: (theme) => `0px solid ${theme.palette.divider}`,
        }}
      >
        <Box className="clinical-field-title" variant="h6" sx={{ color: "text.primary" , mb: 1 , mt: 1}}>
          Select Clinical Field
        </Box>
      </Box>
      <Box className="clinical-field-selection" sx={{}}>
        <FormControl fullWidth focused={false} sx={{ padding: "0px" }}>
          <Select
            labelId="clinical-field-select-label"
            id="clinical-field-select"
            value={selectedFieldId}
            onChange={handleFieldChange}
            sx={{
              backgroundColor: "background.default",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
            }}
            MenuProps={{
              // style the pop-up container
              PaperProps: {
                sx: {
                  bgcolor: "background.default",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  marginTop: "10px",
                  "& .MuiMenuItem-root": {
                    color: "text.primary",
                    bgcolor: (theme) => theme.palette.background.default,
                    "&:hover, &.Mui-selected": {
                      bgcolor: (theme) => theme.palette.background.paper ,
                    },
                  },
                },
              },
            }}
          >
            {fields.map((field) => (
              <MenuItem
                key={field.field_id}
                value={field.field_id}
                sx={{
                  color: "text.primary",
                }}
              >
                {field.field_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Card>
  );
};
